import React, { useEffect, useState } from 'react';
import {
  Button,
  HStack,
  Text,
  Heading,
} from '@chakra-ui/react';
import { QuinielaForm } from '../components/quinielas/QuinielaForm';
import { Match } from '../types';
import {
  filterMatchesForCurrentJornada,
  getCurrentJornada,
  getActiveMundialJornadas,
  getActiveMundialKnockoutPhases,
  formatMundialCaptureLabel,
  getJornadaLabel,
  getMundialGroupMatches,
  getMundialKnockoutMatches,
  sortMatchesForCapture,
  sortMundialKnockoutMatchesForCapture,
} from '../services/matchdayService';
import { quinielaService } from '../services/quinielaService';
import { useAuth } from '../context/AuthContext';
import { useLeague } from '../context/LeagueContext';
import { Link as RouterLink } from 'react-router-dom';
import { rankingService } from '../services/rankingService';
import { mundialTeamService } from '../services/mundial/mundialTeamService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { SportsPageShell } from '../components/ui/SportsPageShell';
import { SportsCard } from '../components/ui/SportsCard';
import { useAppColors } from '../hooks/useAppColors';
import { isUserParticipating } from '../services/userParticipationService';
import { Alert, AlertIcon } from '@chakra-ui/react';

const LEAGUE_TITLES: Record<string, { title: string; subtitle: string; badge: string }> = {
  liga_mx: {
    title: 'Quiniela Liga BBVA MX',
    subtitle: 'Pronostica jornada por jornada y compite con tu grupo',
    badge: 'LIGA MX',
  },
  mundial_fifa: {
    title: 'Quiniela Mundial FIFA 2026',
    subtitle: 'Captura marcadores, sigue el torneo y escala en el ranking',
    badge: 'MUNDIAL 2026',
  },
};

export const HomePage: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [teamLogos, setTeamLogos] = useState<{ [teamName: string]: string }>({});
  const [userPredictions, setUserPredictions] = useState<
    { match_id: string; predicted_score_a: number; predicted_score_b: number }[]
  >([]);
  const { user } = useAuth();
  const { league } = useLeague();
  const leagueInfo = league
    ? LEAGUE_TITLES[league]
    : { title: 'Quinielas', subtitle: '', badge: 'QUINIELAS' };
  const { subtle } = useAppColors();

  const mundialGroupPool =
    league === 'mundial_fifa' ? getMundialGroupMatches(matches) : [];
  const activeGroupMatches =
    league === 'mundial_fifa' ? mundialGroupPool.filter((m) => m.activo) : [];
  const activeKnockoutMatches =
    league === 'mundial_fifa' ? getMundialKnockoutMatches(matches).filter((m) => m.activo) : [];

  const captureMatches =
    league && matches.length > 0
      ? league === 'mundial_fifa'
        ? [
            ...sortMatchesForCapture(activeGroupMatches, league),
            ...sortMundialKnockoutMatchesForCapture(activeKnockoutMatches),
          ]
        : sortMatchesForCapture(filterMatchesForCurrentJornada(matches, league), league)
      : [];

  const activeMundialJornadas =
    league === 'mundial_fifa' && matches.length > 0
      ? getActiveMundialJornadas(mundialGroupPool)
      : [];

  const activeKnockoutPhases =
    league === 'mundial_fifa' && matches.length > 0
      ? getActiveMundialKnockoutPhases(getMundialKnockoutMatches(matches))
      : [];

  const jornadaLabel =
    league === 'mundial_fifa'
      ? formatMundialCaptureLabel(activeMundialJornadas, activeKnockoutPhases)
      : league && matches.length > 0
        ? getJornadaLabel(league, getCurrentJornada(matches, league))
        : '';
  const userParticipates = !user || !league || isUserParticipating(user, league);

  useEffect(() => {
    if (!league) return;
    const fetchMatches = async () => {
      try {
        const data = await quinielaService.getActiveMatches(league);
        setMatches(Object.values(data).flat());
      } finally {
        setIsLoading(false);
      }
    };
    fetchMatches();
  }, [league]);

  useEffect(() => {
    const fetchLogos = async () => {
      const logos: { [teamName: string]: string } = {};
      if (league === 'mundial_fifa') {
        const teams = await mundialTeamService.getAllTeams();
        const byCode = mundialTeamService.getTeamsByCode(teams);
        for (const match of matches) {
          if (match.team_a_code) {
            const t = byCode.get(match.team_a_code.toUpperCase());
            if (t) logos[match.team_a] = t.flag_url;
          }
          if (match.team_b_code) {
            const t = byCode.get(match.team_b_code.toUpperCase());
            if (t && match.team_b) logos[match.team_b] = t.flag_url;
          }
          if (match.team_a_logo) logos[match.team_a] = match.team_a_logo;
          if (match.team_b && match.team_b_logo) logos[match.team_b] = match.team_b_logo;
        }
      } else {
        for (const match of matches) {
          if (match.team_a && !logos[match.team_a]) {
            logos[match.team_a] = (await rankingService.getTeamLogo(match.team_a)) || '';
          }
          if (match.team_b && !logos[match.team_b]) {
            logos[match.team_b] = (await rankingService.getTeamLogo(match.team_b)) || '';
          }
        }
      }
      setTeamLogos(logos);
    };
    if (matches.length > 0 && league) fetchLogos();
  }, [matches, league]);

  useEffect(() => {
    const fetchUserPredictions = async () => {
      if (!user || !league) return;
      const snapshot = await getDocs(
        query(
          collection(db, 'predictions'),
          where('user_id', '==', user.uid),
          where('league', '==', league)
        )
      );
      setUserPredictions(snapshot.docs.map((d) => d.data() as typeof userPredictions[0]));
    };
    fetchUserPredictions();
  }, [user, league]);

  const refreshUserPredictions = async () => {
    if (!user || !league) return;
    const snapshot = await getDocs(
      query(
        collection(db, 'predictions'),
        where('user_id', '==', user.uid),
        where('league', '==', league)
      )
    );
    setUserPredictions(snapshot.docs.map((d) => d.data() as typeof userPredictions[0]));
  };

  return (
    <SportsPageShell
      title={leagueInfo.title}
      subtitle={leagueInfo.subtitle}
      badge={leagueInfo.badge}
    >
      <SportsCard accent p={{ base: 4, md: 5 }}>
        <Heading size="md" mb={1}>
          Captura tu quiniela
        </Heading>
        <Text fontSize="sm" color={subtle} mb={4}>
          3 pts marcador exacto · 1 pt resultado correcto
        </Text>
        {!userParticipates ? (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            Tu participación en esta quiniela está desactivada. Si crees que es un error,
            contacta al administrador.
          </Alert>
        ) : (
        <QuinielaForm
          matches={captureMatches}
          league={league ?? undefined}
          jornadaLabel={jornadaLabel}
          allGroupMatches={league === 'mundial_fifa' ? mundialGroupPool : undefined}
          onSubmit={refreshUserPredictions}
          isLoading={isLoading}
          teamLogos={teamLogos}
          userPredictions={userPredictions}
        />
        )}
      </SportsCard>

      <HStack mt={6} spacing={4} flexWrap="wrap">
        {league === 'mundial_fifa' && (
          <Button as={RouterLink} to="/mundial" colorScheme="pitch" w={{ base: 'full', sm: 'auto' }} flex={{ base: undefined, sm: 1 }} minW={{ sm: '200px' }}>
            Torneo y grupos
          </Button>
        )}
        <Button as={RouterLink} to="/rankings" variant="outline" colorScheme="pitch" w={{ base: 'full', sm: 'auto' }} flex={{ base: undefined, sm: 1 }} minW={{ sm: '200px' }}>
          Ver ranking
        </Button>
        <Button
          as={RouterLink}
          to="/quiniela/quinielas-capturadas"
          variant="ghost"
          colorScheme="pitch"
          w={{ base: 'full', sm: 'auto' }}
          flex={{ base: undefined, sm: 1 }}
          minW={{ sm: '200px' }}
        >
          Quinielas del grupo
        </Button>
      </HStack>
    </SportsPageShell>
  );
};
